class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.isWholesaleCustomer = this.dataset.wholesale === 'true';
    this.cartPageUrl =
      this.dataset.cartUrl ||
      (typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : `${window.origin}/cart`);

    if (this.isWholesaleCustomer) {
      this.setupWholesaleRedirect();
      return;
    }

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindDiscountHandler();
  }

  
  bindDiscountHandler() {
    const discountField = this.querySelector('[data-discount]');
    if (!discountField) return;

    const button = discountField.querySelector('#apply-discount');
    const input = discountField.querySelector('#discount-code');
    const feedback = discountField.querySelector('#discount-feedback');

    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';

    const setFeedback = (message, isError = true) => {
      if (!feedback) return;
      feedback.textContent = message;
      feedback.hidden = !message;
      feedback.classList.toggle('is-error', Boolean(message) && isError);
    };

    button.addEventListener('click', async () => {
      const code = input?.value.trim();
      if (!code) {
        setFeedback('Please enter a discount code.');
        return;
      }

      const url = `https://cp.boldapps.net/v2/api/bb92d5-2.myshopify.com/discount_code?code=${encodeURIComponent(code)}`;
      const storageKey = 'PRE.shopifyDiscountCodes_bb92d5-2.myshopify.com';

      button.disabled = true;
      setFeedback('');

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const data = await response.json();
        const isEmpty =
          data == null ||
          (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0) ||
          (Array.isArray(data) && data.length === 0);

        if (isEmpty) {
          setFeedback('Invalid discount code.');
          sessionStorage.removeItem(storageKey);
        } else {
          const normalizedData = Array.isArray(data) ? data : [data];
          sessionStorage.setItem(storageKey, JSON.stringify(normalizedData));
          setFeedback('');
          await this.refreshCart();
        }
      } catch (error) {
        console.error('Failed to validate discount code:', error);
        setFeedback('Invalid discount code.');
      } finally {
        button.disabled = false;
      }
    });
  }

  async refreshCart() {
    try {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart:', error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.isWholesaleCustomer) {
      window.location.href = this.cartPageUrl;
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.isWholesaleCustomer) {
      window.location.href = this.cartPageUrl;
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.bindDiscountHandler();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setupWholesaleRedirect() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      window.location.href = this.cartPageUrl;
    });

    cartLink.addEventListener('keydown', (event) => {
      const key = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (key === 'SPACE' || key === 'ENTER') {
        event.preventDefault();
        window.location.href = this.cartPageUrl;
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
